import { useEffect, useMemo, useState, useCallback } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Loader2, Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { YearCalendar, CalendarLegend, DayMenu, NamedBreaks } from "@/components/YearCalendar";
import { toast } from "sonner";

export default function DistrictCalendar() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [days, setDays] = useState([]);
  const [allowSchoolEdit, setAllowSchoolEdit] = useState(true);
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState(null);
  const [form, setForm] = useState({ date: "", end_date: "", type: "holiday", label: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (y) => {
    setLoading(true);
    try {
      const [cal, settings] = await Promise.all([
        api.get(`/corporate/calendar?year=${y}`),
        api.get("/corporate/calendar-settings"),
      ]);
      setDays(cal.data);
      setAllowSchoolEdit(settings.data.allow_school_edit);
    } catch { /* no schools yet */ } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(year); }, [year, load]);

  const entriesMap = useMemo(() => Object.fromEntries(days.map((d) => [d.date, { ...d, district: true }])), [days]);

  const pick = async (type) => {
    const { date } = menu;
    setMenu(null);
    try {
      await api.post("/corporate/calendar", { date, type });
      load(year);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    }
  };

  const clearDay = async () => {
    const { date } = menu;
    setMenu(null);
    try {
      await api.delete(`/corporate/calendar/${date}`);
      setDays((ds) => ds.filter((d) => d.date !== date));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove");
    }
  };

  const addRange = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error("Pick a date"); return; }
    setBusy(true);
    try {
      await api.post("/corporate/calendar", { date: form.date, end_date: form.end_date || undefined, type: form.type, label: form.label.trim() });
      toast.success("Calendar days added for every school");
      setForm({ date: "", end_date: "", type: "holiday", label: "" });
      load(year);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  const toggleSchoolEdit = async (v) => {
    setAllowSchoolEdit(v);
    try {
      await api.put("/corporate/calendar-settings", { allow_school_edit: v });
      toast.success(v ? "School admins can now edit their own calendars" : "School calendars are now district-managed only");
    } catch (err) {
      setAllowSchoolEdit(!v);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    }
  };

  return (
    <section data-testid="corporate-calendar-section">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">District calendar</h2>
          <p className="text-sm text-slate-600">Click any day to mark it a holiday or teachers work day for <b>every school</b> in the district.</p>
        </div>
        <div className="flex items-center gap-2" data-testid="district-calendar-year-nav">
          <button onClick={() => setYear((y) => y - 1)} data-testid="district-calendar-prev-year"
            className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 inline-flex items-center justify-center transition">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="font-display text-xl font-bold text-slate-900 w-16 text-center" data-testid="district-calendar-year">{year}</span>
          <button onClick={() => setYear((y) => y + 1)} data-testid="district-calendar-next-year"
            className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 inline-flex items-center justify-center transition">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-sm">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
          <CalendarLegend />
          <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 cursor-pointer" data-testid="allow-school-edit-toggle">
            <div>
              <div className="text-sm font-semibold text-slate-900">School admins can edit their own calendar</div>
              <div className="text-[11px] text-slate-500">When off, only the corporate office can change school calendars</div>
            </div>
            <Switch checked={allowSchoolEdit} onCheckedChange={toggleSchoolEdit} data-testid="allow-school-edit-switch" />
          </label>
        </div>
        {loading ? (
          <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
        ) : (
          <>
            <YearCalendar year={year} entries={entriesMap} onDayClick={(date, entry, e) => setMenu({ date, entry, x: e.clientX, y: e.clientY })} />
            <NamedBreaks entries={days} />
          </>
        )}
        <div className="mt-4 text-xs text-slate-500 inline-flex items-center gap-1.5">
          <CalendarDays className="h-3.5 w-3.5" /> {days.length} district-wide non-school day{days.length === 1 ? "" : "s"} in {year}
        </div>
      </div>

      <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="district-calendar-add-card">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-4">
          <Plus className="h-3.5 w-3.5" /> Add a range (breaks, long weekends) — applies to every school
        </div>
        <form onSubmit={addRange} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <label className="block">
            <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Name (optional)</span>
            <input type="text" maxLength={80} value={form.label} placeholder="e.g. Thanksgiving Break"
                   onChange={(e) => setForm({ ...form, label: e.target.value })}
                   data-testid="district-calendar-label" className={inputCls} />
          </label>
          <label className="block">
            <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">From</span>
            <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                   data-testid="district-calendar-from" className={inputCls} />
          </label>
          <label className="block">
            <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">To (optional)</span>
            <input type="date" value={form.end_date} min={form.date || undefined} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                   data-testid="district-calendar-to" className={inputCls} />
          </label>
          <label className="block">
            <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Type</span>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                    data-testid="district-calendar-type" className={inputCls}>
              <option value="holiday">Holiday for all</option>
              <option value="teacher_workday">Teachers work day</option>
            </select>
          </label>
          <button type="submit" disabled={busy} data-testid="district-calendar-add-btn"
                  className="h-10 inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white hover:shadow disabled:opacity-60 transition">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
          </button>
        </form>
      </section>

      <DayMenu menu={menu} locked={false} onPick={pick} onClear={clearDay} onClose={() => setMenu(null)} />
    </section>
  );
}

const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";
